import type { ReactNode } from 'react';
import {
  Avatar,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { DoorOpen, LayersThree02, XClose } from '@kula-ai/icons-core';
import { Flex } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import {
  isJobRoleToken,
  isOrgRoleToken,
} from '@/utils/internalEmailRecipients';
import { internalEmailJobRoleTooltip } from '@/constants/on-job-roles';
import { roleBracketLabel } from './useRecipientSections';

export interface RecipientChipOption {
  value: string;
  label: string;
  image?: string;
  /** Their roles on this job, which the chip names in brackets. */
  tags?: string[];
}

const ORG_ROLE_SUFFIX = /\s*\(on Kula\)\s*$/i;

/**
 * An org-role chip reaches us with its label in one of two shapes: bare
 * ("Recruiters") from the picker's own sections, or already suffixed from
 * `useInternalEmailRecipientOptions`, which is what the composer hands to
 * kula's `Select` — and `commit` prefers those processed options. Strip first
 * so both spellings converge, rather than producing "Recruiters (on Kula)
 * (on Kula)" for one of them.
 */
const orgRoleName = (label: string) => label.replace(ORG_ROLE_SUFFIX, '');

/**
 * A chip carries no section header, so a bare "Recruiters" reads as either an
 * org role or a job role. Job-role labels already end in "(on job)"; the Kula
 * equivalent is normalised to exactly one suffix here, so the picker's own
 * "Kula roles" section can stay unsuffixed.
 */
const chipLabel = (option: RecipientChipOption) => {
  if (isOrgRoleToken(option.value)) {
    return `${orgRoleName(option.label)} (on Kula)`;
  }

  // A person the Job roles section resolved: the name alone does not say what
  // put them in the list, and on a job the role is why they are there.
  const roles = roleBracketLabel(option.tags);
  if (roles && !isJobRoleToken(option.value)) {
    return `${option.label} (${roles})`;
  }

  return option.label;
};

/**
 * The same person, with every role spelled out — what the hover says when the
 * label had to abbreviate them.
 *
 * `chipLabel` abbreviates on purpose: a chip is 160px wide and the roles sit in
 * brackets after the name, so `roleBracketLabel` keeps the first and counts the
 * rest. The hover has the room the chip does not, and "& +2" is the one part of
 * that label a reader cannot resolve for themselves.
 *
 * Only where there is more to say. One role is already whole in the label, so
 * repeating it would make the hover say nothing new — and a role chip has its own
 * sentence below, which this must not displace.
 */
const chipRolesTooltip = (option: RecipientChipOption) => {
  if (isOrgRoleToken(option.value) || isJobRoleToken(option.value)) return null;

  const tags = option.tags ?? [];
  if (tags.length < 2) return null;

  return `${option.label} (${tags.join(', ')})`;
};

/**
 * What a role chip says on hover: who it actually resolves to, which the label
 * itself never says.
 *
 * A Kula role is named by its own name, which the account chose. A job role is
 * named by the token — its label is design copy ("All hiring managers"), and a
 * sentence built from it read "All users with the All hiring managers role".
 *
 * A user chip has no such sentence — its tooltip would only repeat the name — so
 * it gets none, and kula's `Text` shows the name only when the chip is too narrow
 * to hold it. A tooltip that repeats what is already on screen is noise.
 */
const roleTooltip = (option: RecipientChipOption) => {
  if (isOrgRoleToken(option.value)) {
    return `All users with the ${orgRoleName(option.label)} role in Kula`;
  }
  if (isJobRoleToken(option.value)) {
    return internalEmailJobRoleTooltip(option.value);
  }
  return null;
};

const chipIcon = (option: RecipientChipOption): ReactNode => {
  if (isOrgRoleToken(option.value)) {
    return <DoorOpen size={16} color={colors['secondary-700']} />;
  }
  if (isJobRoleToken(option.value)) {
    return <LayersThree02 size={16} color={colors['secondary-700']} />;
  }
  return (
    <Avatar size={16} shape="square" src={option.image} name={option.label} />
  );
};

/**
 * One selected To/Cc/Bcc value, rendered through kula's `renderTag` seam.
 *
 * Users, job roles and Kula roles share one multi-select but are three
 * different things, so each gets its own mark — an avatar, the layers glyph and
 * the Kula-role glyph. The kind is read off the value token rather than a flag
 * on the option: tokens are the one thing that survives a reload, when chips
 * are rebuilt from the saved activity rather than from the picker.
 */
export const RecipientChip = ({
  option,
  onRemove,
}: {
  option: RecipientChipOption;
  onRemove: () => void;
}) => {
  const label = chipLabel(option);
  const tooltip = roleTooltip(option);
  const rolesTooltip = chipRolesTooltip(option);

  const chip = (
    <Flex
      align="center"
      gap="4px"
      px="8px"
      h="28px"
      maxW="160px"
      bg="white"
      border="1px solid"
      borderColor="secondary-100"
      borderRadius="6px"
      data-test-id={`recipient-chip-${option.value}`}
    >
      {/* Fixed box, never shrinks: as a plain flex item the glyph is squeezed
          by a long label, so chips ended up with visibly different icon sizes.
          The label is what gives way instead — hence `minW`, without which its
          `min-width: auto` refuses to shrink and pushes the loss back here. */}
      <Flex
        align="center"
        justify="center"
        boxSize="16px"
        flexShrink={0}
        // On top of the row's 4px gap, so the mark sits 6px off its label —
        // matching the picker rows — while the remove button keeps the
        // tighter 4px the design gives it.
        mr="2px"
        data-test-id="recipient-chip-icon"
      >
        {chipIcon(option)}
      </Flex>
      {/* `isTruncated`, not `noOfLines={1}`: the latter is `-webkit-line-clamp`
          with no `nowrap`, so the label still wraps at WORD boundaries and the
          clamp shows only the first one — "Hiring Managers (on job)" cut to
          "Hiring…" while "Recruiters (on job)" kept its whole first word. Same
          pixel width, wildly different character counts.

          kula's `Text` opts a truncating label INTO a tooltip of its own unless
          told not to, and it opens that only when the label actually overflows.
          That is exactly what a user chip wants — the name, and only when the
          chip is too narrow to show it. A role chip has the sentence below
          instead, so it turns kula's off rather than showing two at once.

          Handing it a NODE swaps what that tooltip says without changing when it
          says it: kula gates on `openRequested && isOverflowing` either way, and
          only falls back to the label when `tooltip` is `undefined` or `true`. So
          a multi-role user still hovers on overflow alone, and gets the roles the
          label had to count instead of name. */}
      <Text
        variant="p"
        truncate
        tooltip={tooltip ? false : (rolesTooltip ?? undefined)}
        style={{ minWidth: 0 }}
      >
        {label}
      </Text>
      <Flex
        as="button"
        type="button"
        tabIndex={-1}
        align="center"
        flexShrink={0}
        opacity={0.7}
        _hover={{ opacity: 1 }}
        onClick={onRemove}
        aria-label={`Remove ${label}`}
      >
        <XClose size={14} color={colors['secondary-700']} />
      </Flex>
    </Flex>
  );

  if (!tooltip) return chip;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent side="bottom" collisionPadding={12}>
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
